import { LocalDate } from '@js-joda/core';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { FeatureFlag } from '#/shared/components/FeatureFlag';
import { PaidBreakExceptionAction } from '#/shared/components/PaidBreakException';
import { useAnalytics } from '#/shared/hooks/useAnalytics';
import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';

import { RejectionNoteModal } from './RejectionNoteModal';

type PaidBreakExceptionFormFieldProps = {
  shiftDate: LocalDate;
  isLastShift?: boolean;
};

export function PaidBreakExceptionFormField({
  shiftDate,
  isLastShift = false,
}: Readonly<PaidBreakExceptionFormFieldProps>) {
  const exception = usePaidBreakExceptionStore(useShallow(state => state.getException(shiftDate)));
  const setExceptionDecision = usePaidBreakExceptionStore(state => state.setExceptionDecision);
  const setExceptionNotes = usePaidBreakExceptionStore(state => state.setExceptionNotes);
  const decision = exception?.approverDecision ?? 'pending';

  const { track } = useAnalytics();
  const timesheetId = useTimesheetStore(state => state.timesheet?.timesheetId ?? '');

  const [isRejectionNoteModalOpen, setIsRejectionNoteModalOpen] = React.useState(false);

  return (
    <FeatureFlag name="PPL-2119-paid-break-exception">
      <tr
        key={`paid-break-${shiftDate.toString()}`}
        className={!isLastShift ? 'border-b border-outline-l1' : ''}
        /**
         * These attributes below are used to scroll to the paid break exception field when it is invalid
         */
        data-paid-break-exception-field
        data-shift-date={shiftDate.toString()}
      >
        <td colSpan={4} className="px-16 pb-16 pt-4">
          <PaidBreakExceptionAction
            breakDuration={exception?.break.toString() ?? ''}
            status={decision}
            variant="individual"
            reason={exception?.reason}
            notes={exception?.notes}
            approverNotes={exception?.approverNotes}
            onRejectionNoteOpen={() => {
              track('Online approval web - Press Add Rejection Note', {
                Timesheet_ID: timesheetId,
              });
              setIsRejectionNoteModalOpen(true);
            }}
            onRejectionNoteDelete={() => setExceptionNotes(shiftDate, undefined)}
            showRejectionNote={decision === 'rejected'}
            onAccept={() => {
              track('Online approval web - Press Accept', {
                Timesheet_ID: timesheetId,
              });
              setExceptionDecision(shiftDate, 'accepted');
            }}
            onReject={() => {
              track('Online approval web - Press Reject', {
                Timesheet_ID: timesheetId,
              });
              setExceptionDecision(shiftDate, 'rejected');
            }}
            ariaDescribedBy={decision === 'error' ? 'paid-break-exceptions-error' : undefined}
            ariaInvalid={decision === 'error' ? 'true' : undefined}
          />
          <RejectionNoteModal
            isOpen={isRejectionNoteModalOpen}
            shiftStartAt={shiftDate}
            onClose={() => setIsRejectionNoteModalOpen(false)}
          />
        </td>
      </tr>
    </FeatureFlag>
  );
}
